
import { useEffect, useState, useMemo } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { usePlanRestrictions } from "@/hooks/usePlanRestrictions";
import { CheckCircle2, RefreshCw } from "lucide-react";
import { quillImageHandler } from "@/utils/signatureUtils";

export const EmailSignatureEditor = () => {
  const [signature, setSignature] = useState("");
  const [defaultSignature, setDefaultSignature] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const { planLimits } = usePlanRestrictions();

  // Configure Quill modules with custom image handler
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: canEdit ? quillImageHandler : () => {}
      }
    }
  }), [canEdit]);

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'align',
    'link', 'image'
  ];

  useEffect(() => {
    fetchSignature();
  }, []);

  const fetchSignature = async () => {
    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const { data, error } = await supabase.rpc('get_user_signature', {
        p_user_id: session.user.id
      });

      if (error) throw error;
      
      if (data && data.length > 0) {
        setSignature(data[0].signature_html);
        setDefaultSignature(data[0].signature_html);
        setCanEdit(data[0].can_edit);
      }
    } catch (error) {
      console.error("Error fetching signature:", error);
      toast({
        title: "Error",
        description: "Failed to load your email signature",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const saveSignature = async () => {
    setIsSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const { error } = await supabase
        .from('email_signatures')
        .update({ 
          signature_html: signature,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', session.user.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Your email signature has been saved",
      });
      setDefaultSignature(signature);
    } catch (error) {
      console.error("Error saving signature:", error);
      toast({
        title: "Error",
        description: "Failed to save your email signature",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const resetToDefault = () => {
    const defaultMaiaSignature = 'This email has been generated by <a href="https://maia-email.com/" style="color: #0066cc; text-decoration: none;">Maia AI Mail Assistant</a> with and supervised by a Human in the loop.';
    setSignature(defaultMaiaSignature);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Email Signature</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-1/4"></div>
            <div className="h-40 bg-muted rounded w-full"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Email Signature</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {!canEdit && (
            <div className="p-4 rounded-md bg-amber-50 border border-amber-200 text-amber-800 mb-4">
              <p>Free plan users cannot edit their signature. Upgrade to Pro or Premium to customize your email signature.</p>
            </div>
          )}
          
          <div className={!canEdit ? "opacity-70 pointer-events-none" : ""}>
            <ReactQuill
              theme="snow"
              value={signature}
              onChange={setSignature}
              modules={modules}
              formats={formats}
              placeholder="Enter your custom email signature..."
              readOnly={!canEdit}
            />
          </div>
          
          <div className="flex justify-between pt-4">
            <Button
              variant="outline"
              type="button"
              onClick={resetToDefault}
              disabled={!canEdit || isSaving}
            >
              Reset to Default
            </Button>
            
            <Button
              type="button"
              onClick={saveSignature}
              disabled={!canEdit || isSaving || signature === defaultSignature}
            >
              {isSaving ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Save Signature
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
